/**
 * Concrete implementation of `MapServiceBase` which provides a wrapper around *internal* map services - those loaded from the internal data source.
 *
 * @event ObjectLayer#layer-moved
 * @description Fired when a child layer is moved from one index to another.
 * @param {InternalMapService} service A reference to the containing service.
 * @param {ObjectLayer} layer The layer that was moved.
 * @param {Integer} oldIndex The previous index of the layer.
 * @param {Integer} newIndex The new index of the layer.
 *
 * @event ObjectLayer#layer-added
 * @description Fired when a child layer is added to the layer.
 * @param {InternalMapService} service A reference to the containing service.
 * @param {ObjectLayer} layer The layer that was added.
 * @param {Integer} index The index at which the new layer is located.
 *
 * @event ObjectLayer#layer-removed
 * @description Fired when a child layer is removed from this layer.
 * @param {InternalMapService} service A reference to the containing service.
 * @param {ObjectLayer} layer The layer that was removed.
 * @param {Integer} index The original index of the layer.
 *
 * @module app/models/map/InternalMapService
 */
import MapServiceBase from "app/models/map/base/MapServiceBase";
import _ from "underscore";
import $ from "jquery";
import ObjectLayer from "app/models/map/ObjectLayer";
import LayerCollection from "app/models/LayerCollection";
import GeographicSummarizer from "app/models/map/summarize/GeographicSummarizer";
import EditableLayerBase from "app/models/map/EditableLayerBase";
import Field from "core/models/Field";
import ErrorUtils from "app/utils/ErrorUtils";
import session from "app/Session";
import messenger from "app/Messenger";
import mapContext from "app/views/map/MapContext";
//basically, a bundle of featureLayers.
export default MapServiceBase.extend({
  /**
   * Initializes an object.
   * @param  {object} options The options hash.
   * @param {MapService} options.service The map service to associated with this object.
   * @param {UrlBuilder} options.urlBuilder A URL builder scoped to the API base path.
   * @param options.context The context of this instance.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      service: null,
      summarizer: new GeographicSummarizer(),
    });

    /**
     * The map service associated with this object.
     * @type {MapService}
     */
    this.service = this.options.service;
    /**
     * The set of layers associated with this object.
     * @type {app/models/LayerCollection}
     */
    this.featureLayers = new LayerCollection(null, {
      service: this.service,
    });

    /**
     * The summarizer associated with this service.
     * @type {Summarizer}
     */
    this.summarizer = this.options.summarizer;
    this._childLayers = [];
  },

  load: function () {
    var deferred = new $.Deferred();
    var self = this;
    var featureLayers = new LayerCollection(null, {
      service: this.service,
    });

    this.featureLayers = featureLayers;

    featureLayers.fetch({
      success: function (featureLayers) {
        //build a tree of featureLayers.
        //first make sure everything is keyed to id.
        var layerIndex = {};
        var layerChildren = {};

        _.each(featureLayers.models, function (layerModel) {
          layerIndex[layerModel.id] = layerModel;
          layerChildren[layerModel.id] = layerChildren[layerModel.id] || [];

          if (layerModel.get("parent")) {
            var children = layerChildren[layerModel.get("parent")] || [];

            children.push(layerModel.id);

            layerChildren[layerModel.get("parent")] = children;
          }
        });

        function processNode(model, siblings, list) {
          var options = {
            serviceLayer: self,
            featureLayer: model,
            features: model.getItems(),
          };
          var children = layerChildren[model.id];

          if (children && children.length) {
            //recursive case
            options.children = [];
            _.each(children, function (childId) {
              processNode(layerIndex[childId], options.children, list);
            });
            var layer = new ObjectLayer(options);

            siblings.push(new ObjectLayer(options));

            _.each(options.children, function (childLayer) {
              childLayer.parent = layer;
            });
          } else {
            //base case
            siblings.push(new ObjectLayer(options));
          }
        }

        //Coming from the API, the parent layer is always specified on the child layer. The models often lack the child relationship being defined on the parent. To display sublayers, we need the relationship defined on the parent. Iterates over the list and assigns children to their respective parents iff that relationship isn't already defined.
        _.each(layerIndex, function (model, index, list) {
          var parentId = model.getParentId();

          if (parentId) {
            var parent = list[parentId];
            if (parent) {
              if (!parent.get("children")) {
                parent.set("children", []);
              }
              if (
                _.isArray(parent.get("children")) &&
                !_.includes(parent.get("children"), model.id)
              ) {
                parent.get("children").push(model.id);
              }
            }
          }
        });
        //build the tree
        var children = [];

        _.each(layerIndex, function (model) {
          if (!model.get("parent")) {
            processNode(model, children, layerIndex);
          }
        });
        self._childLayers = children;
        deferred.resolve(self);
      },
      error: function (featureLayers, response) {
        deferred.reject(response, self);
      },
    });

    return deferred.promise();
  },

  getLayers: function () {
    return this._childLayers;
  },

  createLayerField: function (onlyIncludeWritableLayers) {
    var self = this;
    var layerField = new Field(
      {
        id: "layer",
        name: "layer",
        label: "Layer",
        values: this.getLayerNames(onlyIncludeWritableLayers),
        editable: function (model) {
          var result = false;

          if (
            model &&
            model.originalLayer &&
            model.originalLayer.featureLayer
          ) {
            result = model.originalLayer.featureLayer.allows(session, "remove");
          }

          return result;
        },
        filterable: true,
        sortable: true,
      },
      {
        getter: function (model) {
          if (model.originalLayer) {
            return (
              model.originalLayer.featureLayer || model.originalLayer.info
            ).id;
          }
        },
        setter: function (model, value) {
          var destinationLayer = null;
          var sourceLayer = model.originalLayer;
          var geometry = sourceLayer.toGeometry(model);

          self.eachLayer(function (layer) {
            if ((layer.featureLayer || layer.info).id == value) {
              destinationLayer = layer;
            }
          });

          EditableLayerBase.moveModel(
            geometry,
            sourceLayer,
            destinationLayer,
            function (err) {
              if (err) {
                ErrorUtils.handleError(null, err);
              } else {
                messenger.success("Feature moved successfully.");
                mapContext.display([model]);
              }
            }
          );
        },
      }
    );

    this.listenTo(this.featureLayers, "add remove", function () {
      layerField.set("values", this.getLayerNames(onlyIncludeWritableLayers));
    });

    return layerField;
  },

  getName: function () {
    return this.service.get("name");
  },

  summarize: function () {
    return this.summarizer.summarizeService(this);
  },

  /**
   * Updates the position attributes of all child layers.
   * @private
   */
  _updateIndicies: function () {
    _.each(this.getLayers(), function (item, index) {
      item.featureLayer.set("position", index);
    });
  },

  /**
   * Move the layer at the given old index to the given new index. If the index is invalid, this function does nothing. The moved layer will have its `position` attribute updated appropriately.
   * @fires InternalMapService#layer-moved
   * @param {Integer} oldIndex The current index of the layer to move.
   * @param {Integer} newIndex The new index at which to place the layer.
   */
  moveLayer: function (oldIndex, newIndex) {
    if (oldIndex >= 0 && oldIndex < this.getLayers().length) {
      var result = this.getLayers().splice(oldIndex, 1);

      this.getLayers().splice(newIndex, 0, result[0]);

      this._updateIndicies();

      this.trigger("layer-moved", this, result[0], oldIndex, newIndex);
    }
  },

  /**
   * Adds a layer at the end of the list.
   * @fires InternalMapService#layer-added
   * @param {ObjectLayer} layer The layer to add. `layer.featureLayer` will have its position, parent, and ownerService attributes updated appropriately.
   * @param {Integer} [index] The index at which to add the layer. By default, layers are added at the end of the list.
   */
  addLayer: function (layer, index) {
    if (!_.isNumber(index)) {
      index = this.getLayers().length;
    }

    this.getLayers().splice(index, 0, layer);
    this.featureLayers.set(
      this.featureLayers.models.splice(index, 0, layer.featureLayer)
    );
    layer.featureLayer.collection = this.featureLayers;

    layer.featureLayer.set({
      service: this.service.id,
      parent: null,
    });

    this._updateIndicies();

    this.trigger("layer-added", this, layer, index);
  },

  /**
   * Removes a layer from the list. This does *not* destroy the layer in the database.
   * @fires InternalMapService#layer-removed
   * @param {ObjectLayer|Integer} layer The layer to remove, or its index. If the index is invalid, no changes will be made.
   * @return {ObjectLayer} The layer that was removed, if any.
   */
  removeLayer: function (layer) {
    if (!_.isNumber(layer)) {
      layer = this.getLayers().indexOf(layer);
    }

    if (layer >= 0 && layer < this.getLayers().length) {
      var result = this.getLayers().splice(layer, 1);

      this.trigger("layer-removed", this, result[0], layer); //reminder that layer is actually the index of the layer

      return result[0];
    }
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/InternalMapService.js
// module id = 508
// module chunks = 1